// Iter 120 — Knowledge Record detail page.
//
// Read-only view of a persisted verified Ask answer with all
// traceability artefacts and operational actions (Share, Export PDF,
// Create Finding / RFI / Submittal). The record itself is immutable;
// these actions create downstream artefacts that link back via the
// existing references[] graph.
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Calendar, User, Trash2, ShieldCheck, FileText, Layers,
  Lightbulb, ExternalLink, Share2, Download, MessageCircle, FilePlus,
  Loader2, Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, API_BASE, getBearer } from "@/lib/api";

export default function KnowledgeRecordDetail() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [shareLink, setShareLink] = useState(null);


  useEffect(() => {
    if (!recordId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/knowledge-records/${recordId}`).then((r) => {
      if (cancelled) return;
      setRec(r.data); setErr(null);
    }).catch((e) => {
      if (cancelled) return;
      setErr(e?.response?.data?.detail || "Record not found.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [recordId]);

  const onConvert = async (kind) => {
    setBusy(kind);
    try {
      const endpoint = {
        finding: `/knowledge-records/${recordId}/create-finding`,
        rfi: `/knowledge-records/${recordId}/create-rfi`,
        submittal: `/knowledge-records/${recordId}/create-submittal`,
      }[kind];
      const payload = (kind === "finding")
        ? { priority: "medium" }
        : { project_id: rec?.project_id || null };
      const r = await api.post(endpoint, payload);
      toast.success(
        kind === "finding"
          ? "Field Finding created with full traceability."
          : `${kind === "rfi" ? "RFI" : "Submittal"} draft ${r.data?.number || ""} created.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not create ${kind} draft.`);
    } finally {
      setBusy(null);
    }
  };

  const onShare = async () => {
    setBusy("share");
    try {
      const r = await api.post(`/knowledge-records/${recordId}/share`, { expires_in_hours: 24 * 30 });
      const url = `${window.location.origin}/k-share/${r.data.token}`;
      setShareLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied. Expires in 30 days.");
      } catch {
        toast.success("Share link ready.");
      }
    } catch (e) {
      toast.error("Could not create share link.");
    } finally {
      setBusy(null);
    }
  };

  const onExport = async () => {
    setBusy("export");
    try {
      const tok = getBearer();
      const resp = await fetch(`${API_BASE}/knowledge-records/${recordId}/export.pdf`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `knowledge-${recordId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Could not export PDF.");
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Permanently delete this knowledge record? Active shares will be revoked. Downstream Findings/RFIs/Submittals will remain.")) return;
    setBusy("delete");
    try {
      await api.delete(`/knowledge-records/${recordId}`);
      toast.success("Record deleted.");
      navigate("/knowledge");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-[var(--fop-text-3)]"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div>;
  }
  if (err) {
    return <div className="p-8"><div className="rounded border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{err}</div></div>;
  }
  if (!rec) return null;

  return (
    <div className="min-h-screen bg-[var(--fop-surface)] text-[var(--fop-text)]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
        <div className="mb-4">
          <Link to="/knowledge" className="inline-flex items-center gap-1 text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="kr-back">
            <ArrowLeft size={12} /> All knowledge records
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Archive size={16} className="text-[var(--fop-primary)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
              Verified knowledge record
            </span>
            <Badge variant="outline" className="font-mono text-[10px]">{rec.confidence}</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" data-testid="kr-question">
            {rec.question}
          </h1>
          <div className="flex items-center gap-3 text-[11px] text-[var(--fop-text-4)] font-mono">
            <span><Calendar size={11} className="inline mr-1" /> {rec.created_at?.slice(0,10)}</span>
            <span><User size={11} className="inline mr-1" /> {rec.created_by_name || "—"}</span>
            <span>· {rec.id}</span>
            {rec.ask_session_id && <span>· session {rec.ask_session_id.slice(-12)}</span>}
          </div>
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="kr-actions">
          <Button variant="outline" size="sm" onClick={() => onConvert("finding")} disabled={!!busy} data-testid="kr-create-finding">
            {busy === "finding" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Lightbulb size={12} className="mr-1.5" />} Create Field Finding
          </Button>
          <Button variant="outline" size="sm" onClick={() => onConvert("rfi")} disabled={!!busy} data-testid="kr-create-rfi">
            {busy === "rfi" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <MessageCircle size={12} className="mr-1.5" />} Create RFI Draft
          </Button>
          <Button variant="outline" size="sm" onClick={() => onConvert("submittal")} disabled={!!busy} data-testid="kr-create-submittal">
            {busy === "submittal" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <FilePlus size={12} className="mr-1.5" />} Create Submittal Draft
          </Button>
          <Button variant="outline" size="sm" onClick={onShare} disabled={!!busy} data-testid="kr-share">
            {busy === "share" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Share2 size={12} className="mr-1.5" />} Share
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} disabled={!!busy} data-testid="kr-export-pdf">
            {busy === "export" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Download size={12} className="mr-1.5" />} Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onDelete} disabled={!!busy} data-testid="kr-delete" className="text-red-300 border-red-500/40 hover:bg-red-500/10">
            <Trash2 size={12} className="mr-1.5" /> Delete
          </Button>
        </div>

        {shareLink && (
          <div className="mb-6 flex items-center gap-2" data-testid="kr-share-link-row">
            <input
              readOnly
              value={shareLink}
              onFocus={(e) => e.target.select()}
              className="flex-1 text-xs font-mono px-3 py-2 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] text-[var(--fop-text-2)]"
              data-testid="kr-share-link-input"
            />
            <a href={shareLink} target="_blank" rel="noreferrer" className="text-xs text-[var(--fop-primary)] hover:underline">
              Open <ExternalLink size={11} className="inline" />
            </a>
          </div>
        )}

        {/* Answer */}
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 mb-6" data-testid="kr-answer">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-300 mb-2 flex items-center gap-1">
            <ShieldCheck size={12} /> Verified answer
          </div>
          <div className="text-base leading-relaxed text-[var(--fop-text)] whitespace-pre-wrap">{rec.answer}</div>
        </div>

        {/* Citations */}
        {(rec.citations || []).length > 0 && (
          <div className="rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)]/50 p-4 mb-6" data-testid="kr-citations">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-3">Why this answer is trusted</div>
            <ul className="space-y-2">
              {rec.citations.map((c, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <Link to={`/spec-section/${c.spec_id}`} className="font-mono text-[var(--fop-primary)] hover:underline mr-2">
                    §{c.section_number}
                  </Link>
                  <span className="text-[var(--fop-text-2)]">{c.section_title}</span>
                  {c.reason && <div className="text-xs text-[var(--fop-text-3)] mt-0.5 ml-7">{c.reason}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Source specifications */}
        {(rec.sources || []).length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-4 mb-2">
              <FileText size={13} className="text-[var(--fop-text-3)]" />
              <h2 className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Source specifications ({rec.sources.length})</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 mb-6" data-testid="kr-sources">
              {rec.sources.map((s) => (
                <div key={s.id} className="rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="font-mono text-[11px]">Section {s.section_number}</Badge>
                  </div>
                  <div className="text-sm font-semibold mb-0.5">{s.section_title || "—"}</div>
                  {(s.page_start || s.page_end) && (
                    <div className="text-xs text-[var(--fop-text-4)]">Pages {s.page_start ?? "—"}–{s.page_end ?? "—"}</div>
                  )}
                  {s.source_document && (
                    <div className="text-xs text-[var(--fop-text-4)] truncate" title={s.source_document}>{s.source_document}</div>
                  )}
                  <div className="flex flex-wrap gap-3 mt-2">
                    <Link to={`/spec-section/${s.id}`} className="text-xs text-[var(--fop-primary)] hover:underline inline-flex items-center gap-1">
                      <Layers size={11} /> Open spec
                    </Link>
                    {s.source_pdf_url && (
                      <a href={s.source_pdf_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--fop-primary)] hover:underline inline-flex items-center gap-1">
                        <ExternalLink size={11} /> Open PDF · page {s.page_start || 1}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Related */}
        {(rec.related?.sheets || []).length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-4 mb-2">
              <Layers size={13} className="text-[var(--fop-text-3)]" />
              <h2 className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Related sheets ({rec.related.sheets.length})</h2>
            </div>
            <div className="space-y-1.5 mb-6">
              {rec.related.sheets.map((s) => (
                <Link key={s.id} to={`/sheet/${s.id}`} className="block rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] px-3 py-2 hover:border-[var(--fop-primary)]">
                  <span className="font-mono text-sm text-[var(--fop-primary)] mr-2">{s.sheet_number}</span>
                  <span className="text-sm">{s.sheet_title || "—"}</span>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
